import { inject, Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { FeathersService } from '../../feathers.service'
import { map, shareReplay } from 'rxjs/operators'
import { Workgroup } from '../../../models/workgroup'
import { SearchUtils } from '../../../shared/utils/searchUtils'

@Injectable({
  providedIn: 'root',
})
export class WorkgroupService {
  private feathers = inject(FeathersService)

  static ALL_WORKGROUPS = 999

  stream$: Observable<Workgroup[]>

  public getAllWithCaching(): Observable<Workgroup[]> {
    if (!this.stream$) this.init()
    return this.stream$
  }

  private init() {
    this.stream$ = this.feathers.find(Workgroup, {}).pipe(
      map((wgs: Workgroup[]) =>
        wgs.sort((a, b) => {
          if (SearchUtils.normalizeForSearch(a.name) < SearchUtils.normalizeForSearch(b.name)) return -1
          if (SearchUtils.normalizeForSearch(a.name) > SearchUtils.normalizeForSearch(b.name)) return 1
          return 0
        }),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  public getAll(): Promise<Workgroup[]> {
    return this.feathers.getPromise(Workgroup, {})
  }

  getById(workgroupId: number): Observable<Workgroup> {
    return this.getAllWithCaching().pipe(map((wgs) => wgs.filter((wg) => wg.id === workgroupId)[0]))
  }

  async create(workgroup: Partial<Workgroup>): Promise<Workgroup> {
    const created = await this.feathers.create<Workgroup>(Workgroup.serviceName, workgroup)
    this.invalidateCache()
    return created
  }

  async patch(workgroupId: number, workgroup: Partial<Workgroup>): Promise<Workgroup> {
    const patched = await this.feathers.patch(Workgroup.serviceName, { id: workgroupId, ...workgroup })
    this.invalidateCache()
    return patched as Workgroup
  }

  async remove(workgroupId: number): Promise<void> {
    await this.feathers.removeById(Workgroup.serviceName, workgroupId)
    this.invalidateCache()
  }

  private invalidateCache(): void {
    this.stream$ = undefined
  }
}
